<template>
    <div
        v-if="show"
        class="fixed inset-0 z-50 flex items-center justify-center"
    >
        <div
            class="absolute inset-0 bg-slate-900/50"
            @click="closeModal"
        ></div>
        <div
            class="relative bg-white dark:bg-slate-800 rounded-lg shadow-lg w-full max-w-sm md:max-w-lg mx-4 p-6"
        >
            <div class="flex items-center justify-between mb-4">
                <h3 class="text-xl font-medium text-slate-900 dark:text-slate-100">
                    {{ title }}
                </h3>
                <button
                    class="p-2 rounded hover:bg-slate-100 dark:hover:bg-slate-700 dark:bg-slate-700 transition-colors"
                    @click="closeModal"
                >
                    <Icon icon="close" class="w-5 h-5 text-slate-500 dark:text-slate-400" />
                </button>
            </div>
            <p
                v-if="description"
                class="text-sm text-slate-500 dark:text-slate-400 mb-6"
            >
                {{ description }}
            </p>

            <div
                id="add-card-element"
                class="mb-6 px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800"
            ></div>

            <div v-if="addPaymentError" class="text-sm text-red-500 mb-4">
                {{ addPaymentError }}
            </div>

            <div class="flex justify-end gap-3">
                <button
                    class="px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-900 dark:text-slate-100 rounded shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900/40 transition-colors"
                    @click="closeModal"
                >
                    Cancel
                </button>
                <button
                    class="px-4 py-2.5 bg-slate-900 text-sm font-medium text-white rounded shadow-sm hover:bg-slate-800 transition-colors disabled:opacity-50"
                    :disabled="addPaymentProcessing"
                    @click="handleAddCard"
                >
                    {{
                        addPaymentProcessing
                            ? 'Processing...'
                            : 'Add credit card'
                    }}
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue';
import { useThemeStore } from '@/store/theme';
import { usePage } from '@inertiajs/vue3';
import Icon from '@/components/ui/icon/icon.vue';

const props = defineProps({
    show: {
        type: Boolean,
        default: false,
    },
    title: {
        type: String,
        default: 'Add payment method',
    },
    description: {
        type: String,
        default: '',
    },
});

const emit = defineEmits(['update:show', 'card-added']);

const page = usePage();

const addPaymentProcessing = ref(false);
const addPaymentError = ref('');

const themeStore = useThemeStore();
let stripeInstance = null;
let cardElement = null;

const initStripeCardElement = async () => {
    await nextTick();
    const stripeKey = page.props.stripe_key;
    if (!stripeKey) return;

    stripeInstance = window.Stripe(stripeKey);
    const elements = stripeInstance.elements();
    cardElement = elements.create('card', {
        style: {
            base: {
                fontSize: '14px',
                color: themeStore.isDark ? '#f1f5f9' : '#1e293b',
                '::placeholder': { color: '#94a3b8' },
            },
        },
    });
    cardElement.mount('#add-card-element');
};

const closeModal = () => {
    emit('update:show', false);
    addPaymentError.value = '';
    if (cardElement) {
        cardElement.destroy();
        cardElement = null;
    }
    stripeInstance = null;
};

const handleAddCard = async () => {
    if (!stripeInstance || !cardElement) return;

    addPaymentProcessing.value = true;
    addPaymentError.value = '';

    try {
        const { paymentMethod, error } =
            await stripeInstance.createPaymentMethod({
                type: 'card',
                card: cardElement,
            });

        if (error) {
            addPaymentError.value = error.message;
            return;
        }

        await ApiService.apiCall('/travel-plus/payment-method', 'post', {
            payment_method: paymentMethod.id,
        });

        closeModal();
        emit('card-added');
    } catch (e) {
        addPaymentError.value =
            e?.response?.data?.message ||
            'Something went wrong. Please try again.';
    } finally {
        addPaymentProcessing.value = false;
    }
};

watch(
    () => props.show,
    (val) => {
        if (val) {
            initStripeCardElement();
        }
    }
);
</script>
